<script setup lang="ts">
  import { watch } from 'vue'
  import type { ResolvedField } from '../../model/types'
  import type { SetFieldConfigRequestPayload } from '@gat-ship/types'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z,
    Alert,
    AlertDescription
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { IconAlertCircle, IconInfo, IconLocked } from '@gat-ship/icons'
  import FieldEditableConfigActionCard from '../FieldEditableConfigActionCard.vue'
  import FieldValidationConfigActionCard from '../FieldValidationConfigActionCard.vue'
  import FieldVisibilityConfigActionCard from '../FieldVisibilityConfigActionCard.vue'

  defineProps<{
    field: ResolvedField | null
  }>()

  const emit = defineEmits<{
    (e: 'save-config', payload: SetFieldConfigRequestPayload): void
    (e: 'reset-config'): void
  }>()

  const open = defineModel<boolean>('open', { default: false })

  // ── Form ──────────────────────────────────────────────────────

  const configSchema = z.object({
    hint: z.string(),
    requiredOption: z.enum(['optional', 'required']),
    visibilityOption: z.enum(['visible', 'visible-for-role', 'hidden']),
    visibleForRoleIds: z.array(z.string()),
    editableOption: z.enum(['editable', 'editable-for-role', 'disabled']),
    editableForRoleIds: z.array(z.string())
  })

  type ConfigFormValues = z.infer<typeof configSchema>

  const form = useForm({
    defaultValues: {
      hint: '',
      requiredOption: 'optional',
      visibilityOption: 'visible',
      visibleForRoleIds: [],
      editableOption: 'editable',
      editableForRoleIds: []
    } as ConfigFormValues,
    validators: {
      onChange: configSchema
    },
    onSubmit: async ({ value }) => {
      const payload: SetFieldConfigRequestPayload = {
        hint: value.hint || null,
        requiredOption: value.requiredOption,
        visibilityOption: value.visibilityOption,
        visibleForRoleIds:
          value.visibilityOption === 'visible-for-role'
            ? value.visibleForRoleIds
            : null,
        editableOption: value.editableOption,
        editableForRoleIds:
          value.editableOption === 'editable-for-role'
            ? value.editableForRoleIds
            : null
      }

      emit('save-config', payload)
      open.value = false
    }
  })

  // ── Sync form when dialog opens ──────────────────────────────

  function populateForm(field: ResolvedField) {
    form.setFieldValue('hint', field.hint ?? '')
    form.setFieldValue('requiredOption', field.requiredOption)
    form.setFieldValue('visibilityOption', field.visibilityOption)
    form.setFieldValue('visibleForRoleIds', field.visibleForRoleIds ?? [])
    form.setFieldValue('editableOption', field.editableOption)
    form.setFieldValue('editableForRoleIds', field.editableForRoleIds ?? [])
  }

  function show(field: ResolvedField) {
    form.reset()
    populateForm(field)
    open.value = true
  }

  watch(open, (val) => {
    if (!val) form.reset()
  })

  defineExpose({ show })

  // ── Reset ────────────────────────────────────────────────────

  function handleReset() {
    emit('reset-config')
    open.value = false
  }
</script>

<template>
  <modal v-model:open="open" type="dialog">
    <ModalHeader>
      <ModalHeaderTitle>Configure "{{ field?.label }}"</ModalHeaderTitle>
      <ModalHeaderDescription>
        <span class="flex items-center gap-1.5">
          <span
            class="bg-muted inline-block rounded px-2 py-0.5 text-xs font-medium"
          >
            {{ field?.fieldType ?? 'system' }}
          </span>
          <span
            v-if="field?.requiredByDeveloper"
            class="text-muted-foreground flex items-center gap-1 text-xs"
          >
            <IconLocked class="h-3 w-3" />
            Limited configuration
          </span>
        </span>
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalSection>
      <ModalContent class="flex flex-col gap-4">
        <FieldGroup>
          <FormField
            :form="form"
            name="hint"
            label="Hint"
            hint="A tooltip hint shown to the end user - just like this one!"
          >
            <template #default="{ field: hintField }">
              <InputGroupInput
                :id="hintField.name"
                :model-value="hintField.state.value ?? ''"
                @update:model-value="hintField.handleChange"
                @blur="hintField.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
        <Alert
          v-if="field?.requiredByDeveloper"
          variant="info"
          class="border-none"
        >
          <IconInfo class="size-4 shrink-0" />
          <AlertDescription>
            This field is required by Gatship and has limited configuration
            options.
          </AlertDescription>
        </Alert>
        <h3 v-if="!field?.requiredByDeveloper" class="text-sm font-medium">
          Field Options
        </h3>
        <FieldValidationConfigActionCard
          v-if="!field?.requiredByDeveloper && field?.inputType !== 'checkbox'"
          :form="form"
        />

        <FieldVisibilityConfigActionCard
          v-if="!field?.requiredByDeveloper"
          :form="form"
        />
        <FieldEditableConfigActionCard
          v-if="!field?.requiredByDeveloper"
          :form="form"
        />
        <Alert
          v-if="
            form.getFieldValue('requiredOption') === 'required' &&
            form.getFieldValue('editableOption') === 'disabled'
          "
          class="border-none"
          variant="warning"
        >
          <IconAlertCircle class="size-4 shrink-0" />
          <AlertDescription>
            This field is required, but is not editable which may cause issues
            when creating new entities.
          </AlertDescription>
        </Alert>
      </ModalContent>
    </ModalSection>
    <ModalFooter>
      <Button
        v-if="field?.hasConfigOverride"
        variant="outline"
        size="lg"
        @click="handleReset"
      >
        Reset
      </Button>
      <Button class="ml-auto" variant="ghost" size="lg" @click="open = false">
        Cancel
      </Button>
      <FormSubmit :form="form" label="Save Config" />
    </ModalFooter>
  </modal>
</template>
